import { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Progress,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { MeContext } from '../../../context/MeContext';
import sentry from '../../../sentry';
import CustomToast from '../../../common/CustomToast';
import {
  useGetOnboardingProfileQuery,
  useUpdateOnboardingProfileMutation,
} from '../../../store/redux/api/onboardingApi';
import type {
  JobRole,
  OnboardingProfilePayload,
} from '../../../types/onboarding';
import {
  EMPTY_SURVEY_VALUES,
  MAX_OTHER_TEXT_LENGTH,
  getSurveySteps,
  isQuestionSatisfied,
  type OnboardingSurveyFormValues,
} from './onboardingSurveyQuestions';
import OnboardingSurveyField from './OnboardingSurveyField';
import { useOnboardingSurveyGate } from './useOnboardingSurveyGate';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';

const OnboardingSurveyModal = () => {
  const isReadOnly = useIsReadOnly();
  const shouldShow = useOnboardingSurveyGate();
  const meCtx = useContext(MeContext) as {
    state: { account: { job_role?: JobRole; lead_user?: boolean } | null };
  };
  const toast = useToast();

  const { data: profileResponse, isFetching: isPrefillFetching } =
    useGetOnboardingProfileQuery(undefined, { skip: !shouldShow });
  const [submitProfile, { isLoading: isSubmitting }] =
    useUpdateOnboardingProfileMutation();

  const [step, setStep] = useState(0);
  const [values, setValues] =
    useState<OnboardingSurveyFormValues>(EMPTY_SURVEY_VALUES);
  const [submitted, setSubmitted] = useState(false);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!profileResponse || hydratedRef.current) return;
    hydratedRef.current = true;
    const { job_role, profile } = profileResponse;
    setValues({
      job_role: job_role ?? meCtx.state.account?.job_role ?? '',
      primary_use_case: profile.primary_use_case ?? [],
      primary_use_case_other: profile.primary_use_case_other ?? '',
      build_intent: profile.build_intent ?? '',
      expected_volume: profile.expected_volume ?? '',
      industry: profile.industry ?? '',
      country: profile.country ?? '',
    });
  }, [profileResponse, meCtx.state.account]);

  if (!shouldShow) return null;
  if (isReadOnly) return null;

  const isLead = Boolean(meCtx.state.account?.lead_user);
  const steps = getSurveySteps(isLead);
  const questions = steps.flat();
  const totalSteps = steps.length;
  const currentStep = Math.min(step, totalSteps - 1);
  const stepQuestions = steps[currentStep];
  const canProceed = stepQuestions.every((q) => isQuestionSatisfied(q, values));
  const isLastStep = currentStep === totalSteps - 1;

  const setValue = (
    id: keyof OnboardingSurveyFormValues,
    value: string | string[]
  ) => setValues((prev) => ({ ...prev, [id]: value }));

  const handleBack = () => setStep((s) => Math.max(0, s - 1));
  const handleNext = () => setStep((s) => Math.min(totalSteps - 1, s + 1));

  const handleSubmit = async () => {
    const firstGap = questions.findIndex(
      (q) => !isQuestionSatisfied(q, values)
    );
    if (firstGap !== -1) {
      setStep(
        steps.findIndex((qs) => qs.some((q) => !isQuestionSatisfied(q, values)))
      );
      return;
    }

    const payload = questions.reduce(
      (acc, q) => {
        acc[q.id] = values[q.id];

        if (q.otherText) {
          const answer = values[q.id];
          const otherValue = values[q.otherText.key];
          if (
            Array.isArray(answer) &&
            answer.includes(q.otherText.showWhen) &&
            typeof otherValue === 'string' &&
            otherValue.trim()
          ) {
            acc[q.otherText.key] = otherValue.trim();
          }
        }
        return acc;
      },
      {} as Record<string, string | string[]>
    );

    try {
      await submitProfile(
        payload as Partial<OnboardingProfilePayload>
      ).unwrap();
      setSubmitted(true);
      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title="Thanks! You're all set."
            description=""
            onClose={props.onClose}
          />
        ),
      });
    } catch (error) {
      sentry.sendError(error);
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Something went wrong"
            description="Please try again."
            onClose={props.onClose}
          />
        ),
      });
    }
  };

  const busy = isSubmitting || submitted;

  return (
    <Modal
      isOpen
      onClose={() => undefined}
      isCentered
      size={{ base: 'full', md: 'xl' }}
      scrollBehavior="inside"
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent
        aria-labelledby="onboarding-survey-title"
        borderRadius={{ base: 0, md: '20px' }}
        overflow="hidden"
        bg="white"
        boxShadow="0 24px 60px -16px rgba(17,22,34,0.55), 0 6px 18px -4px rgba(232,93,58,0.35)"
      >
        <ModalBody p={{ base: 0, md: 0 }}>
          {/* ── Orange gradient header ──────────────────────────────────── */}
          <Box
            background="onboardingBgGradient"
            color="white"
            px={{ base: 6, md: 8 }}
            pt={10}
            pb={7}
            textAlign="center"
          >
            <Text
              id="onboarding-survey-title"
              textStyle="heading3"
              fontWeight="800"
              color="white"
              mb={2}
            >
              Welcome to PassEntry
            </Text>

            <Text textStyle="body1" color="whiteAlpha.900" mx="auto" mb={5}>
              {isLead
                ? "You're in. A few quick questions so we can tailor your setup."
                : "You're in. One quick question to set up your profile."}
            </Text>

            <Box mx="auto" maxW="260px">
              <Progress
                value={((currentStep + 1) / totalSteps) * 100}
                size="xs"
                borderRadius="full"
                bg="whiteAlpha.400"
                sx={{ '& > div': { background: 'white' } }}
              />
            </Box>
            <Text
              fontFamily="heading"
              fontSize="12px"
              letterSpacing="2px"
              textTransform="uppercase"
              fontWeight="600"
              color="whiteAlpha.900"
              mt={2}
            >
              Step {currentStep + 1} of {totalSteps}
            </Text>
          </Box>

          {/* ── White body: the step's two questions + navigation ───────── */}
          <Box bg="white" color="inkBlue" px={{ base: 6, md: 8 }} py={7}>
            {isPrefillFetching ? (
              <Flex justify="center" py={10}>
                <Spinner />
              </Flex>
            ) : (
              <Flex direction="column" gap={7}>
                {stepQuestions.map((question) => {
                  const labelId = `survey-q-${question.id}`;
                  const otherText = question.otherText;
                  const answer = values[question.id];
                  const showOther =
                    otherText &&
                    Array.isArray(answer) &&
                    answer.includes(otherText.showWhen);
                  return (
                    <Box key={question.id}>
                      <Flex align="baseline" gap={2} mb={4} wrap="wrap">
                        <Text
                          id={labelId}
                          textStyle="bodyMedium"
                          fontWeight="600"
                          color="inkBlue"
                        >
                          {question.prompt}
                        </Text>
                        {question.helper && (
                          <Text fontFamily="body" fontSize="14px" color="grey">
                            {question.helper}
                          </Text>
                        )}
                      </Flex>

                      <OnboardingSurveyField
                        question={question}
                        value={values[question.id]}
                        onChange={(value) => setValue(question.id, value)}
                        labelId={labelId}
                      />

                      {otherText && showOther && (
                        <FormControl isRequired mt={3}>
                          <Input
                            value={values[otherText.key] as string}
                            onChange={(e) =>
                              setValue(otherText.key, e.target.value)
                            }
                            placeholder={otherText.placeholder}
                            aria-label={otherText.placeholder}
                            maxLength={MAX_OTHER_TEXT_LENGTH}
                            borderRadius="12px"
                            borderColor="lightGrey"
                            _hover={{ borderColor: 'orangePrimary' }}
                            _focus={{
                              borderColor: 'orangePrimary',
                              boxShadow: 'none',
                            }}
                            _focusVisible={{
                              borderColor: 'orangePrimary',
                              boxShadow: 'none',
                            }}
                          />
                          <FormHelperText mt={1}>Required field</FormHelperText>
                        </FormControl>
                      )}
                    </Box>
                  );
                })}
              </Flex>
            )}

            <Flex mt={8} justify="space-between" align="center" gap={4}>
              <Button
                variant="ghost"
                onClick={handleBack}
                isDisabled={currentStep === 0 || busy}
                visibility={currentStep === 0 ? 'hidden' : 'visible'}
              >
                Back
              </Button>
              {isLastStep ? (
                <Button
                  variant="gradient"
                  onClick={handleSubmit}
                  isLoading={busy}
                  isDisabled={!canProceed || isPrefillFetching}
                >
                  Get Started
                </Button>
              ) : (
                <Button
                  variant="gradient"
                  onClick={handleNext}
                  isDisabled={!canProceed || isPrefillFetching}
                >
                  Next
                </Button>
              )}
            </Flex>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default OnboardingSurveyModal;
